/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React, { Component } from 'react';
import { Table, Input, InputNumber, Form } from 'antd';
import colors from '../colors';
import { AIcon as Icon } from '../icon';

// AntD6 migration (AER-1648): the old implementation rendered each editable
// cell through an AntD3 `getFieldDecorator` pulled off a React context. AntD6
// removed `getFieldDecorator`, and the context value was always `undefined`
// here, so every body cell did `undefined(...)`/destructured `undefined` and
// threw on render -> React 19 unmounted the whole tree -> white screen (seen
// when selecting an "Aeras One" equipment type, which renders the Alert
// Thresholds table). We now bind editable cells through a real AntD6 form
// instance via `<Form.Item name>`.
class EditableCell extends Component {
  getInput = () => {
    if (this.props.inputType === 'number') {
      return <InputNumber />;
    }
    return <Input />;
  };

  render() {
    const {
      editing,
      dataIndex,
      title,
      inputType,
      record,
      index,
      children,
      isRequired,
      ...restProps
    } = this.props;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: isRequired,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {this.getInput()}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
}

class EditableTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: props.data,
      editingKey: '',
    };
    // One AntD6 form instance per table, shared by the editable cells.
    // AntD6 exposes the FormInstance through a ref passed to <Form ref>.
    this.form = React.createRef();
  }

  isEditing = record => record.key === this.state.editingKey;

  cancel = () => {
    this.setState({ editingKey: '' });
    if (this.props.setEditing) {
      this.props.setEditing(false);
    }
  };

  save(key) {
    const form = this.form.current;
    if (!form) {
      return;
    }
    form
      .validateFields()
      .then(row => {
        const newData = [...this.props.dataSource];
        const index = newData.findIndex(item => key === item.key);
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        this.setState({ editingKey: '' }, () => {
          this.props.onSave(newData, key, index);
          this.props.setEditing(false);
        });
      })
      .catch(() => undefined);
  }

  edit(record) {
    const form = this.form.current;
    if (form) {
      // Seed the form with the row's current values so the inputs start
      // populated when the user enters edit mode (AntD6 binds via `name`).
      form.setFieldsValue({ ...record });
    }
    this.setState({ editingKey: record.key });
    if (this.props.setEditing) {
      this.props.setEditing(true);
    }
  }

  render() {
    const components = {
      body: {
        cell: EditableCell,
      },
    };

    const columns = [
      ...this.props.columns,
      {
        title: ' ',
        dataIndex: 'operation',
        render: (text, record) => {
          const { editingKey } = this.state;
          const editable = this.isEditing(record);
          return editable ? (
            <span>
              <a onClick={() => this.save(record.key)} style={{ marginRight: 8 }}>
                Save
              </a>
              <a onClick={() => this.cancel(record.key)}>Cancel</a>
            </span>
          ) : (
            <Icon
              css={css(`color: ${colors.highlight}`)}
              type="edit"
              disabled={editingKey !== ''}
              onClick={() => this.edit(record)}
            />
          );
        },
      },
    ].map(col => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: record => ({
          record,
          inputType: this.props.inputType || 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: this.isEditing(record),
          isRequired: this.props.isRequired === undefined || this.props.isRequired,
        }),
      };
    });

    return (
      <Form ref={this.form} component={false}>
        <Table
          {...this.props}
          size={'small'}
          components={components}
          columns={columns}
          rowClassName={(item, index) => `tbl-list-row-${index % 2} editable-row`}
          pagination={false}
        />
      </Form>
    );
  }
}

export const EditableFormTable = EditableTable;
